import type { KeyboardEvent, ReactNode } from 'react'
import { cx } from '../cx'
import s from './Table.module.css'

export interface Column<T> {
  key: string
  label: string
  /** Numbers align right, in mono. */
  numeric?: boolean
  /** The row's name. When the table is narrow, rows stack and this one leads each row. Default: the first column. */
  primary?: boolean
  render: (row: T) => ReactNode
}

/**
 * Rows of things. A table when there's room; stacked rows when it's narrow (a phone, a drawer, a half-width card),
 * chosen by its own width. `empty` is required: say why it's empty and what would fill it.
 *
 * With `onOpen`, rows open (click, tap, Enter) and the arrow keys or j and k move between them, so a list with a
 * drawer works from the keyboard. `selected` marks the open row.
 */
export function Table<T>(props: {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  empty: ReactNode
  /** What the table lists, for screen readers ("Top products"). */
  label?: string
  onOpen?: (row: T) => void
  selected?: string | null
}) {
  if (props.rows.length === 0) return <>{props.empty}</>
  const primary = props.columns.findIndex((c) => c.primary)
  const lead = primary === -1 ? 0 : primary
  const open = props.onOpen

  const onKey = (e: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    const tr = e.currentTarget
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      open?.(row)
    } else if (e.key === 'ArrowDown' || e.key === 'j') {
      e.preventDefault()
      ;(tr.nextElementSibling as HTMLElement | null)?.focus()
    } else if (e.key === 'ArrowUp' || e.key === 'k') {
      e.preventDefault()
      ;(tr.previousElementSibling as HTMLElement | null)?.focus()
    }
  }

  return (
    <div className={s.room}>
      <table className={cx(s.table, open && s.interactive)} aria-label={props.label}>
        <thead>
          <tr>
            {props.columns.map((c) => (
              <th key={c.key} scope="col" className={c.numeric ? s.num : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row) => {
            const key = props.rowKey(row)
            return (
              <tr
                key={key}
                data-row={key}
                className={cx(props.selected === key && s.selected)}
                tabIndex={open ? 0 : undefined}
                aria-selected={open ? props.selected === key : undefined}
                onClick={open ? () => open(row) : undefined}
                onKeyDown={open ? (e) => onKey(e, row) : undefined}
              >
                {props.columns.map((c, i) => (
                  <td key={c.key} data-label={c.label} className={cx(c.numeric && s.num, i === lead && s.lead)}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
